window.CollectionTabs = class {
  constructor() {
    this.init();
    this.createOverlay();
  }

  init() {
    this.tabsContainer = document.querySelector('[data-tabs-container]');
    if (!this.tabsContainer) return;

    this.buttons = this.tabsContainer.querySelectorAll('.tab-button');
    this.productGrid = document.querySelector('#product-grid');
    this.collectionTitle = document.querySelector('.collection-hero__title');
    this.collectionDescription = document.querySelector('.collection-hero__description.rte');
    this.additionalTitle = document.querySelector('.rich-text__heading.rte.inline-richtext.h1');
    
    if (!this.productGrid) return;
    
    this.setupEventListeners();
  }

  createOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'tabs-loading-overlay';
    
    const style = document.createElement('style');
    style.textContent = `
      .tabs-loading-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(255, 255, 255, 0.7);
        backdrop-filter: blur(2px);
        display: none;
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: 1000;
      }

      .tabs-loading-overlay.active {
        display: block;
        opacity: 1;
      }

      .tabs-loading-overlay::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 40px;
        height: 40px;
        margin: -20px 0 0 -20px;
        border: 3px solid #FF69B4;
        border-radius: 50%;
        border-top-color: transparent;
        animation: tabsLoadingSpin 0.8s linear infinite;
      }

      @keyframes tabsLoadingSpin {
        to { transform: rotate(360deg); }
      }
    `;
    
    document.head.appendChild(style);
    document.body.appendChild(overlay);
    this.overlay = overlay;
  }

  setupEventListeners() {
    this.buttons.forEach(button => {
      button.addEventListener('click', (e) => this.handleTabClick(e));
    });
  }

  async handleTabClick(e) {
    e.preventDefault();
    const button = e.currentTarget;
    
    if (button.classList.contains('active') || this.isLoading) return;
    
    try {
      this.isLoading = true;
      this.setLoadingState(true);
      this.updateActiveButton(button);

      const currentUrl = new URL(window.location.href);
      const filterParams = new URLSearchParams();
      
      for (const [key, value] of currentUrl.searchParams.entries()) {
        if (key !== 'page' && key !== 'section_id') {
          filterParams.append(key, value);
        }
      }

      const newUrl = new URL(button.href);
      for (const [key, value] of filterParams.entries()) {
        newUrl.searchParams.append(key, value);
      }

      window.history.replaceState({ searchParams: newUrl.searchParams.toString() }, '', newUrl.toString());

      const response = await fetch(newUrl.toString());
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      this.updateCollectionInfo(doc);

      if (window.FacetFiltersForm) {
        await FacetFiltersForm.renderPage(newUrl.searchParams.toString(), null, false);

        await new Promise(resolve => setTimeout(resolve, 800));

        const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        const parsedGrid = parsedDoc.getElementById('product-grid');
        
        if (!parsedGrid?.dataset.totalProducts || !parsedGrid?.dataset.productsPerPage) {
          console.error('Missing required grid data:', parsedGrid?.dataset);
          return;
        }

        const totalProducts = parseInt(parsedGrid.dataset.totalProducts);
        const productsPerPage = parseInt(parsedGrid.dataset.productsPerPage);

        if (totalProducts > productsPerPage) {
          let retryCount = 0;
          const maxRetries = 5;
          
          const checkGridAndInitLoadMore = async () => {
            const grid = document.querySelector('#product-grid');
            if (grid && grid.dataset.totalProducts) {
              const existingLoadMore = document.querySelector('.load-more-section');
              if (existingLoadMore) {
                existingLoadMore.remove();
              }
              
              await new Promise(resolve => setTimeout(resolve, 100));
              new window.LoadMoreProducts();
            } else if (retryCount < maxRetries) {
              retryCount++;
              await new Promise(resolve => setTimeout(resolve, 300));
              await checkGridAndInitLoadMore();
            } else {
              console.error('Failed to initialize load more after maximum retries');
            }
          };

          await checkGridAndInitLoadMore();
        }
      } else {
        this.updatePageContent(html);
      }
      
    } catch (error) {
      console.error('Error loading collection:', error);
      
    } finally {
      this.isLoading = false;
      this.setLoadingState(false);
    }
  }

  updateCollectionInfo(doc) {
    if (this.collectionTitle) {
      const newTitle = doc.querySelector('.collection-hero__title');
      if (newTitle) {
        const visuallyHiddenSpan = this.collectionTitle.querySelector('.visually-hidden');
        const newTitleText = newTitle.textContent.replace('Collection: ', '');
        
        if (visuallyHiddenSpan) {
          this.collectionTitle.innerHTML = `
            <span class="visually-hidden">Collection: </span>${newTitleText}
          `;
        } else {
          this.collectionTitle.textContent = newTitleText;
        }
      }
    }

    if (this.additionalTitle) {
      const newAdditionalTitle = doc.querySelector('.rich-text__heading.rte.inline-richtext.h1');
      if (newAdditionalTitle) {
        const existingAttributes = this.additionalTitle.attributes;
        const newContent = newAdditionalTitle.innerHTML;
        this.additionalTitle.innerHTML = newContent;
      }
    }

    if (this.collectionDescription) {
      const newDescription = doc.querySelector('.collection-hero__description.rte');
      if (newDescription) {
        this.collectionDescription.innerHTML = newDescription.innerHTML;
      } else {
        this.collectionDescription.innerHTML = '';
      }
    }
  }

  updatePageContent(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const newGrid = doc.getElementById('product-grid');
    if (newGrid && this.productGrid) {
      this.productGrid.innerHTML = newGrid.innerHTML;
      this.productGrid.dataset.totalProducts = newGrid.dataset.totalProducts;
      this.productGrid.dataset.productsPerPage = newGrid.dataset.productsPerPage;
    }

    const facetsForm = document.querySelector('facet-filters-form');
    const newFacetsForm = doc.querySelector('facet-filters-form');
    if (facetsForm && newFacetsForm) {
      facetsForm.innerHTML = newFacetsForm.innerHTML;
    }

    if (window.LoadMoreProducts) {
      const existingLoadMore = document.querySelector('.load-more-section');
      if (existingLoadMore) {
        existingLoadMore.remove();
      }
      new window.LoadMoreProducts();
    }
  }

  updateActiveButton(activeButton) {
    this.buttons.forEach(btn => btn.classList.remove('active'));
    activeButton.classList.add('active');
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.overlay.classList.add('active');
    } else {
      this.overlay.classList.remove('active');
    }
  }
};

document.addEventListener('DOMContentLoaded', () => {
  new window.CollectionTabs();
});